import { Controller } from '@hotwired/stimulus';
import { Turbo } from '@hotwired/turbo-rails';
import { Modal } from 'bootstrap';
import { create, supported } from '@github/webauthn-json';
import { postJson, postTurboStream } from '../../lib/passkeys';

export default class extends Controller {
  static targets = ['submit', 'error'];
  static values = {
    challengeUrl: String,
    createUrl: String,
    nickname: String,
    cancelledMessage: String,
    genericMessage: String,
  };

  async connect() {
    if (!supported() || !(await this.#deviceCanEnrol())) return;

    this.modalElement = this.element.querySelector('.modal');

    if (!this.modalElement) return;

    this.modal = new Modal(this.modalElement);
    this.modal.show();

    this.modalElement.addEventListener(
      'hidden.bs.modal',
      () => {
        this.modal.dispose();
        this.modal = null;
        this.element.remove();
      },
      { once: true }
    );
  }

  dismiss(event) {
    event.preventDefault();
    this.modal?.hide();
  }

  async enrol() {
    this.#hideError();
    this.submitTarget.disabled = true;

    try {
      const options = await this.#requestChallenge();

      if (options) {
        await this.#persist(await create({ publicKey: options }));
      }
    } catch (error) {
      this.#showError(this.#messageFor(error));
    } finally {
      this.submitTarget.disabled = false;
    }
  }

  #deviceCanEnrol() {
    return window.PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable?.();
  }

  async #requestChallenge() {
    const response = await postJson(this.challengeUrlValue, {});

    if (!response.ok) {
      this.#showError(this.genericMessageValue);
      return null;
    }

    return response.json();
  }

  async #persist(credential) {
    const response = await postTurboStream(this.createUrlValue, {
      users_passkey: { nickname: this.nicknameValue, credential: credential },
    });

    if (!response.ok) {
      this.#showError(this.genericMessageValue);
      return;
    }

    Turbo.renderStreamMessage(await response.text());
    this.modal?.hide();
  }

  #messageFor(error) {
    if (error.name === 'NotAllowedError' || error.name === 'AbortError') {
      return this.cancelledMessageValue;
    }

    return this.genericMessageValue;
  }

  #showError(message) {
    // Reveal before writing: a mutation inside display:none is never announced.
    this.errorTarget.classList.remove('d-none');
    this.errorTarget.textContent = message;
  }

  #hideError() {
    this.errorTarget.textContent = '';
    this.errorTarget.classList.add('d-none');
  }
}
